import JSZip from 'jszip';

import { removeSpaces } from '@common/utils/fileNameHelpers';
import { formatSheet } from '@common/xlsxExport/xlsxExport';
import { gridToCsv, normalizePeriodCells } from '@common/csvExport/csvExport';
import { SheetExportTypes, HEADINGS_ROW_HEIGHT } from '@common/xlsxExport/xlsxExport.constants';

import saveAs from '../../shared/saveAs';
import { viewTypes, groupTypes, COLUMNS, OPTION_ALL } from './constants';
import getColumnDefs from './getColumnDefs';
import { BOOK_TYPES } from '../shared/constants';

const sanitizeSheetName = name => (name || 'sheet').replace(/[/\\?%*:|"<>]/g, '_').slice(0, 200);

const getFilteredColumnDefs = (
  reportBookType,
  groupType,
  groupItem,
  hasSourceCurrency,
  useHistorical,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus
) =>
  getColumnDefs(reportBookType, groupType, groupItem, {
    hasSourceCurrency,
    useHistorical,
    shouldDisplayZero,
    hideDecimals,
    negativeMinus,
  })
    .filter(({ hide }) => !hide);

const buildSheetData = ({
  reportData,
  detailRows,
  viewType,
  selectedReportBookTypes,
  groupType,
  groupItem,
  countryTaxTypeDescription,
  hasSourceCurrency,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
  useHistorical,
  useExpandableDetailsList,
}) => {
  const selectedBookTypes = selectedReportBookTypes || Object.keys(reportData || {});

  const bookTypeSheetMeta = {
    [BOOK_TYPES.US_SUMMARY]: { name: 'US Summary', bookType: BOOK_TYPES.US_SUMMARY },
    [BOOK_TYPES.US_FED]: { name: 'US Federal', bookType: BOOK_TYPES.US_FED },
    [BOOK_TYPES.US_STATE]: { name: countryTaxTypeDescription === OPTION_ALL.label ? 'US State' : countryTaxTypeDescription, bookType: BOOK_TYPES.US_STATE },
    [BOOK_TYPES.TED_NON_US_GAAP]: { name: 'GAAP Non-US', bookType: BOOK_TYPES.TED_NON_US_GAAP },
    [BOOK_TYPES.TED_NON_US_STAT]: { name: 'STAT Non-US', bookType: BOOK_TYPES.TED_NON_US_STAT },
  };

  return selectedBookTypes
    .map(bookType => {
      if (useHistorical && bookType === BOOK_TYPES.US_SUMMARY) {
        return null;
      }

      const report = reportData?.[bookType];
      if (!report) {
        return null;
      }

      const meta = bookTypeSheetMeta[bookType];
      if (!meta) {
        return null;
      }

      if (viewType === COLUMNS) {
        return {
          name: meta.name,
          data: report,
          type: SheetExportTypes.REPORT,
          descriptionColumnIndex: useExpandableDetailsList ? 2 : 1,
          options: {
            omitExportIfNull: true,
            numberOfColumnsToStick: 3,
            numberOfAdditionalRowsToStick: 3,
            detailRows: detailRows?.[bookType],
            shouldDisplayZero,
            hideDecimals,
            negativeMinus,
            addTimestamp: true,
            splitDescriptionColumn: true,
            isFunctionalCurrency: hasSourceCurrency,
            groupByTag: useExpandableDetailsList,
          },
        };
      }

      return {
        name: meta.name,
        data: report.rawSource,
        columnDefinitions: getFilteredColumnDefs(
          meta.bookType,
          groupType,
          groupItem,
          hasSourceCurrency,
          useHistorical,
          shouldDisplayZero,
          hideDecimals,
          negativeMinus
        ),
        type: SheetExportTypes.GRID,
        additionalMetadata: [
          { value: report.name, height: HEADINGS_ROW_HEIGHT },
        ],
        options: {
          numberOfColumnsToStick: 1,
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
          addTimestamp: [BOOK_TYPES.US_SUMMARY, BOOK_TYPES.US_FED].includes(bookType),
          headerRowHeight: 60,
        },
      };
    })
    .filter(Boolean);
};

const buildCsvSheets = (sheetData, description) => {
  const formatter = formatSheet({ description });
  const usedNames = new Set();

  return sheetData.map(sheet => {
    const { grid } = formatter(sheet);
    const baseName = sanitizeSheetName(sheet.name);
    let name = baseName;
    let counter = 1;
    while (usedNames.has(name)) {
      name = `${baseName}_${counter}`;
      counter += 1;
    }
    usedNames.add(name);

    return { name, content: gridToCsv(normalizePeriodCells(grid)) };
  });
};

const downloadSingleCsv = (csvSheet, fileName) => {
  const blob = new Blob([csvSheet.content], { type: 'text/csv;charset=utf-8' });
  saveAs({ blob, filenameWithExtension: `${fileName}.csv` });
};

const downloadCsvZip = (csvSheets, fileName) => {
  const zip = new JSZip();
  csvSheets.forEach(csvSheet => {
    zip.file(`${csvSheet.name}.csv`, csvSheet.content);
  });

  return zip.generateAsync({ type: 'blob' })
    .then(blob => {
      saveAs({ blob, filenameWithExtension: `${fileName}.zip` });
    });
};

export default ({
  period,
  reportData,
  detailRows,
  viewType,
  selectedReportBookTypes,
  groupType,
  groupItem,
  countryTaxTypeDescription,
  hasSourceCurrency,
  reportingCurrencyCode,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
  useHistorical,
  environmentSuffix,
  useExpandableDetailsList = false,
}) => {
  const sheetData = buildSheetData({
    reportData,
    detailRows,
    viewType,
    selectedReportBookTypes,
    groupType,
    groupItem,
    countryTaxTypeDescription,
    hasSourceCurrency,
    shouldDisplayZero,
    hideDecimals,
    negativeMinus,
    useHistorical,
    useExpandableDetailsList,
  });

  if (sheetData.length === 0) {
    return null;
  }

  const chosenViewType = viewTypes.find(type => type.value === viewType);
  const chosenGroupType = groupTypes.find(type => type.value === groupType);
  const description = `${period.name}, ${chosenGroupType.label}, Currency: ${reportingCurrencyCode}, ${chosenViewType.label}`;
  const reportName = `${useHistorical ? 'Historical_' : ''}Tax_Expense`;
  const fileName = removeSpaces(`TLS_${reportName}_${period.name}_${chosenGroupType.label}_${reportingCurrencyCode}${environmentSuffix}`);

  const csvSheets = buildCsvSheets(sheetData, description);

  if (csvSheets.length === 1) {
    downloadSingleCsv(csvSheets[0], fileName);
    return null;
  }

  return downloadCsvZip(csvSheets, fileName);
};
